import { useCallback } from 'react';
import PropTypes from 'prop-types';

import { ConnectedSweepstakesBanner as SweepstakesBanner } from 'app/marketplace/SlideInBanners/SweepstakesBanner/ConnectedSweepstakesBanner';
import { hasSlideIn, isMobileViewport } from 'app/shared/utils/utils';
import { ConnectedCrmBanner as CrmBanner } from 'app/marketplace/SlideInBanners/CrmBanner/ConnectedCrmBanner';
import { ConnectedUserSurvey as UserSurvey } from 'app/marketplace/SlideInBanners/UserSurveyBanner/ConnectedUserSurvey';
import {
  PAGES_WITH_SLIDE_IN_BANNER_DESKTOP,
  PAGES_WITH_SLIDE_IN_BANNER_MOBILE,
  PAGES_WITH_SWEEPSTAKES_BANNER_DESKTOP,
  PAGES_WITH_USER_SURVEY_BANNER_DESKTOP,
  PAGES_WITH_USER_SURVEY_BANNER_MOBILE,
} from 'app/config';

export const SlideInBannersController = ({
  pathname,
  breakpoint,
  showSlideIn,
  slideInBannersFlag172,
  slideInBannersFlag169,
  slideInDismissedCookie,
  target,
  crmSlideInFormSubmitted,
  hasSignedUpForNewsletter,
}) => {
  const isMobile = isMobileViewport(breakpoint);

  const defineBanner = useCallback(() => {
    // For the CRM variant
    const crmSlideInPages = PAGES_WITH_SLIDE_IN_BANNER_DESKTOP.split(',');
    const crmSlideIn = hasSlideIn(pathname, !showSlideIn, crmSlideInPages);

    const userSurveyPages = PAGES_WITH_USER_SURVEY_BANNER_DESKTOP.split(',');
    const userSurveySlideIn = hasSlideIn(
      pathname,
      !showSlideIn,
      userSurveyPages,
    );
    // For the Sweepstakes variant
    const sweepstakesSlideInPages = PAGES_WITH_SWEEPSTAKES_BANNER_DESKTOP.split(
      ',',
    );
    const sweepstakesSlideIn = hasSlideIn(
      pathname,
      !showSlideIn,
      sweepstakesSlideInPages,
    );

    switch (slideInBannersFlag172) {
      case 'show_crm_variant':
        return (
          crmSlideIn.show && (
            <CrmBanner
              threshold={crmSlideIn.threshold}
              submitted={crmSlideInFormSubmitted || hasSignedUpForNewsletter}
              target={target}
              isDesktopVariant
            />
          )
        );

      case 'show_user_survey_variant':
        return (
          userSurveySlideIn.show && (
            <UserSurvey
              threshold={userSurveySlideIn.threshold}
              submitted={crmSlideInFormSubmitted || hasSignedUpForNewsletter}
              target={target}
              isDesktopVariant
            />
          )
        );

      case 'show_sweepstakes_variant':
        return (
          sweepstakesSlideIn.show && (
            <SweepstakesBanner
              threshold={crmSlideIn.threshold}
              submitted={crmSlideInFormSubmitted || hasSignedUpForNewsletter}
            />
          )
        );

      default:
        // The control variant is expected to return nothing
        return null;
    }
  }, [
    slideInBannersFlag172,
    crmSlideInFormSubmitted,
    hasSignedUpForNewsletter,
  ]);

  const defineBannerMobile = useCallback(() => {
    // For the CRM variant
    const crmSlideInPages = PAGES_WITH_SLIDE_IN_BANNER_MOBILE.split(',');
    const crmSlideIn = hasSlideIn(pathname, !showSlideIn, crmSlideInPages);

    const userSurveyPages = PAGES_WITH_USER_SURVEY_BANNER_MOBILE.split(',');
    const userSurveySlideIn = hasSlideIn(
      pathname,
      !showSlideIn,
      userSurveyPages,
    );

    switch (slideInBannersFlag169) {
      case 'show_crm_variant':
        return (
          crmSlideIn.show && (
            <CrmBanner
              threshold={crmSlideIn.threshold}
              submitted={crmSlideInFormSubmitted || hasSignedUpForNewsletter}
              target={target}
              isMobileVariant
            />
          )
        );

      case 'show_user_survey_variant':
        return (
          userSurveySlideIn.show && (
            <UserSurvey
              threshold={userSurveySlideIn.threshold}
              submitted={crmSlideInFormSubmitted || hasSignedUpForNewsletter}
              target={target}
              isMobileVariant
            />
          )
        );

      default:
        // The control variant is expected to return nothing
        return null;
    }
  }, [
    slideInBannersFlag169,
    crmSlideInFormSubmitted,
    hasSignedUpForNewsletter,
  ]);

  // Hide any slide-in banner if dismissed; value comes from both state and cookie
  if (!showSlideIn || slideInDismissedCookie) return null;

  return isMobile ? defineBannerMobile() : defineBanner();
};

SlideInBannersController.propTypes = {
  // what page are we on here
  target: PropTypes.string.isRequired,
  pathname: PropTypes.string,
  showSlideIn: PropTypes.bool,
  breakpoint: PropTypes.string,
  areCookiesAccepted: PropTypes.bool,
  slideInDismissedCookie: PropTypes.bool,
  crmSlideInFormSubmitted: PropTypes.bool,
  slideInBannersFlag172: PropTypes.string,
  slideInBannersFlag169: PropTypes.string,
  hasSignedUpForNewsletter: PropTypes.bool,
};

SlideInBannersController.defaultProps = {
  pathname: null,
  breakpoint: 'sm',
  showSlideIn: false,
  areCookiesAccepted: false,
  slideInBannersFlag172: null,
  slideInDismissedCookie: false,
  crmSlideInFormSubmitted: false,
  hasSignedUpForNewsletter: false,
};
